import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(localStorage.getItem('pulse_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const setToken = (newToken) => {
    if (newToken) {
      localStorage.setItem('pulse_token', newToken);
    } else {
      localStorage.removeItem('pulse_token');
    }
    setTokenState(newToken);
  };

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('pulse_token');
    const savedUser = localStorage.getItem('pulse_user');
    
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
      const response = await authAPI.me();
      setUser(response.data);
      localStorage.setItem('pulse_user', JSON.stringify(response.data));
    } catch (err) {
      localStorage.removeItem('pulse_token');
      localStorage.removeItem('pulse_user');
      setUser(null);
      setTokenState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      localStorage.setItem('pulse_user', JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      // 403 means email not verified
      if (status === 403) {
        return { success: false, error: message, unverified: true, email };
      }
      return { success: false, error: message };
    }
  };

  const register = async (data) => {
    setError(null);
    try {
      const response = await authAPI.register(data);
      // Registration no longer returns a token; user must verify email first
      return { success: true, email: response.data.email, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const verifyRegistration = async (email, code) => {
    setError(null);
    try {
      const response = await authAPI.verifyRegistration({ email, code });
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      localStorage.setItem('pulse_user', JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Verification failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    // Clear impersonation data if present
    localStorage.removeItem('originalToken');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('pulse_token');
    localStorage.removeItem('pulse_user');
    setUser(null);
    setTokenState(null);
  };

  const returnFromImpersonation = () => {
    const originalToken = localStorage.getItem('originalToken');
    if (originalToken) {
      setToken(originalToken);
      localStorage.removeItem('originalToken');
      localStorage.removeItem('impersonating');
      // Force page reload to refresh context
      window.location.href = '/admin/customers';
    }
  };

  const isImpersonating = () => {
    return !!localStorage.getItem('impersonating');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      token, 
      setToken,
      loading, 
      error, 
      login, 
      register, 
      verifyRegistration,
      logout, 
      checkAuth,
      returnFromImpersonation,
      isImpersonating
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
